const STYLE_ID = 'stark-mobile-collection-slider-styles';

function ensureSliderStyles() {
  if (document.getElementById(STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @media (max-width: 749px) {
      stark-collection-slider .stark-collections__viewport {
        margin-inline: -16px;
      }

      stark-collection-slider .stark-collections__track {
        gap: 10px;
        padding-inline: 16px;
        scroll-padding-inline: 16px;
        overscroll-behavior-inline: contain;
      }

      stark-collection-slider .stark-collection-card {
        min-width: calc((100% - 10px) / 2);
        flex: 0 0 calc((100% - 10px) / 2);
      }

      stark-collection-slider .stark-collection-card__media {
        aspect-ratio: 5 / 4;
      }

      stark-collection-slider .stark-collection-card__media img,
      stark-collection-slider .stark-collection-card__media svg {
        object-position: center;
      }

      stark-collection-slider .stark-collection-card__copy {
        min-height: 126px;
        padding: 14px 13px 15px;
      }

      stark-collection-slider .stark-collection-card h3 {
        min-height: 2.6em;
      }
    }
  `;
  document.head.append(style);
}

class StarkCollectionSlider extends HTMLElement {
  connectedCallback() {
    ensureSliderStyles();

    this.track = this.querySelector('[data-slider-track]');
    this.previous = this.querySelector('[data-slider-previous]');
    this.next = this.querySelector('[data-slider-next]');
    this.status = this.querySelector('[data-slider-status]');
    this.dots = this.querySelector('[data-slider-dots]');
    this.cards = [...this.querySelectorAll('.stark-collection-card')];

    if (!this.track || !this.cards.length) return;

    this.previous?.addEventListener('click', () => this.goTo(this.page - 1));
    this.next?.addEventListener('click', () => this.goTo(this.page + 1));
    this.track.addEventListener('scroll', () => this.queueUpdate(), { passive: true });

    this.resizeObserver = new ResizeObserver(() => this.setup());
    this.resizeObserver.observe(this.track);
    this.setup();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  get cardsPerPage() {
    const firstCard = this.cards[0];
    if (!firstCard) return 1;

    const cardWidth = firstCard.getBoundingClientRect().width;
    const styles = getComputedStyle(this.track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
    const availableWidth = this.track.clientWidth;

    return Math.max(1, Math.floor((availableWidth + gap) / (cardWidth + gap)));
  }

  getCardScrollPosition(card) {
    const trackRect = this.track.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const paddingStart = Number.parseFloat(getComputedStyle(this.track).paddingInlineStart) || 0;

    return cardRect.left - trackRect.left + this.track.scrollLeft - paddingStart;
  }

  buildPagePositions() {
    const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const positions = [0];
    const perPage = this.cardsPerPage;

    for (let index = perPage; index < this.cards.length; index += perPage) {
      const position = Math.max(0, Math.min(this.getCardScrollPosition(this.cards[index]), maxScroll));
      const previousPosition = positions[positions.length - 1];

      if (Math.abs(position - previousPosition) > 2) positions.push(position);
    }

    if (maxScroll > 2 && Math.abs(maxScroll - positions[positions.length - 1]) > 2) {
      positions.push(maxScroll);
    }

    return positions;
  }

  setup() {
    const previousPage = this.page || 0;
    this.pagePositions = this.buildPagePositions();
    this.pageCount = this.pagePositions.length;
    this.page = Math.min(previousPage, this.pageCount - 1);
    this.buildDots();
    this.update();
  }

  buildDots() {
    if (!this.dots) return;
    this.dots.replaceChildren();

    for (let index = 0; index < this.pageCount; index += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      const pageLabel = this.dataset.pageLabel || 'Ir a la página [n]';
      dot.setAttribute('aria-label', pageLabel.replace('[n]', String(index + 1)));
      dot.addEventListener('click', () => this.goTo(index));
      this.dots.append(dot);
    }
  }

  goTo(page) {
    const targetPage = Math.max(0, Math.min(page, this.pageCount - 1));
    const left = this.pagePositions[targetPage];
    if (left === undefined) return;

    this.track.scrollTo({ left, behavior: 'smooth' });
  }

  queueUpdate() {
    if (this.frame) return;

    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  }

  update() {
    const currentScroll = this.track.scrollLeft;
    let nearestPage = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;

    this.pagePositions.forEach((position, index) => {
      const distance = Math.abs(currentScroll - position);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestPage = index;
      }
    });

    this.page = nearestPage;

    if (this.previous) this.previous.disabled = this.page === 0;
    if (this.next) this.next.disabled = this.page >= this.pageCount - 1;
    if (this.status) this.status.textContent = `${this.page + 1} / ${this.pageCount}`;

    [...(this.dots?.children || [])].forEach((dot, index) => {
      const isActive = index === this.page;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }
}

if (!customElements.get('stark-collection-slider')) {
  customElements.define('stark-collection-slider', StarkCollectionSlider);
}
